import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  Text,
  Badge,
  Input,
  Textarea,
  Spinner,
  Stack,
  Code,
  HStack,
  SimpleGrid,
  Checkbox,
  Menu,
  Portal,
} from '@chakra-ui/react';
import { Table } from '@chakra-ui/react';
import { NativeSelect } from '@chakra-ui/react';
import { Alert } from '@chakra-ui/react';
import { api, Company, Branch, DeviceSystem, DeviceStatus, EncryptionKeyResponse, SoftwareVersion } from '../api/api';
import { useConfirmation, useNotification } from '../context/NotificationContext';
import { FormControl, FormLabel, FormHelperText, FormErrorMessage } from '../components/FormControls';
import { Panel, PanelBody, PanelHeader } from '../components/Panel';
import SimpleModal from '../components/SimpleModal';
import ZatcaXmlArchiveModal from '../components/ZatcaXmlArchiveModal';
import ZatcaTaxSummaryModal from '../components/ZatcaTaxSummaryModal';
import { EditIcon, PlusIcon, TrashIcon } from '../components/Icons';
import { useSearchParams } from 'react-router-dom';

// Helper to format "time ago" (data is stored in UTC)
function timeAgoSaudi(dateString: string | undefined): string {
  if (!dateString) return 'Never';
  
  // Parse the UTC timestamp
  const dateUtc = new Date(dateString);
  
  // Get current time in UTC
  const nowUtc = new Date();
  
  // Calculate difference in milliseconds
  const diffMs = nowUtc.getTime() - dateUtc.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  
  if (diffSec < 60) return `${diffSec} sec${diffSec !== 1 ? 's' : ''} ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min${diffMin !== 1 ? 's' : ''} ago`;
  const diffHour = Math.floor(diffMin / 60);
  const remainingMin = diffMin % 60;
  if (diffHour < 24) {
    if (remainingMin === 0) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
    return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ${remainingMin} min ago`;
  }
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) return `${diffMonth} month${diffMonth !== 1 ? 's' : ''} ago`;
  const diffYear = Math.floor(diffMonth / 12);
  return `${diffYear} year${diffYear !== 1 ? 's' : ''} ago`;
}

function formatSaudiDateTime(dateString: string | undefined): string {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

function formatSaudiDate(dateString: string | undefined): string {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

function getDaysUntilExpiry(expiryDate: string): number {
  const now = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

function calculateLicenseDuration(expiryDate: string) {
  const now = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry.getTime() - now.getTime();

  if (diffTime <= 0) {
    return "License expired";
  }

  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const years = Math.floor(diffDays / 365);
  const months = Math.floor((diffDays % 365) / 30);
  const days = diffDays % 30;

  let result = "";
  if (years > 0) result += `${years} year${years > 1 ? 's' : ''} `;
  if (months > 0) result += `${months} month${months > 1 ? 's' : ''} `;
  if (days > 0 || result === "") result += `${days} day${days !== 1 ? 's' : ''}`;

  return result.trim();
}

const SystemsPage: React.FC = () => {
  const [systems, setSystems] = useState<DeviceSystem[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [allBranches, setAllBranches] = useState<Branch[]>([]);
  const [resellers, setResellers] = useState<any[]>([]);
  const [form, setForm] = useState<Partial<DeviceSystem>>({});
  const [editId, setEditId] = useState<number | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEncryptionKey, setShowEncryptionKey] = useState<{ systemId: number; keyData: EncryptionKeyResponse } | null>(null);
  const [showEncryptionKeyForm, setShowEncryptionKeyForm] = useState(false);
  const [xmlArchiveModal, setXmlArchiveModal] = useState<{ machineKey: string; systemName: string } | null>(null);
  const [taxSummaryModal, setTaxSummaryModal] = useState<{ machineKey: string; systemName: string } | null>(null);
  const [encryptionKeyExpiryDate, setEncryptionKeyExpiryDate] = useState('');
  const [validation, setValidation] = useState({
    company: true,
    branch: true,
    licenseExpiryDate: true,
    systemName: true
  });
  const [latestStatus, setLatestStatus] = useState<Record<string, DeviceStatus>>({});
  const [uploadedXmlKeys, setUploadedXmlKeys] = useState<Set<string>>(new Set());
  const [availableSoftware, setAvailableSoftware] = useState<SoftwareVersion[]>([]);
  const [updating, setUpdating] = useState<Record<number, boolean>>({});
  const [pendingUpdateSelection, setPendingUpdateSelection] = useState<Record<number, string>>({});
  const { confirm } = useConfirmation();
  const { notify } = useNotification();
  const [searchParams] = useSearchParams();
  const didApplyQueryFilters = useRef(false);

  // Filter state
  const [filterCompanyId, setFilterCompanyId] = useState<number | null>(null);
  const [filterBranchId, setFilterBranchId] = useState<number | null>(null);
  const [filterResellerId, setFilterResellerId] = useState<number | null>(null);
  const [filterMachineKey, setFilterMachineKey] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterVat, setFilterVat] = useState('');
  const [filterCrn, setFilterCrn] = useState('');

  // New filters (Saudi date inputs: yyyy-MM-dd)
  const [filterRegistrationFrom, setFilterRegistrationFrom] = useState('');
  const [filterRegistrationTo, setFilterRegistrationTo] = useState('');
  const [filterExpiryFrom, setFilterExpiryFrom] = useState('');
  const [filterExpiryTo, setFilterExpiryTo] = useState('');
  const [filterRegistrationMethod, setFilterRegistrationMethod] = useState('all');
  const [filterExpiryPreset, setFilterExpiryPreset] = useState('all');
  const [filterZatcaStatus, setFilterZatcaStatus] = useState('all');
  const [showInactive, setShowInactive] = useState(false);

  const [serverFiltersNonce, setServerFiltersNonce] = useState(0);

  const fetchLatestStatus = () => {
    api.systemStatus.latest().then(statuses => {
      const map: Record<string, DeviceStatus> = {};
      statuses.forEach(s => { if (s) map[s.machineKey] = s; });
      setLatestStatus(map);
    });
  };

  const fetchUploadedXmlKeys = () => {
    api.zatcaXml.uploadedMachineKeys().then(keys => setUploadedXmlKeys(new Set(keys))).catch(() => {});
  };

  const fetchAvailableSoftware = async () => {
    try {
      const bundle = await api.softwareVersions.grouped();
      setAvailableSoftware(bundle?.versions ?? []);
    } catch (error) {
      console.error('Failed to fetch available software:', error);
    }
  };

  const load = () => {
    api.systems.list({
      resellerId: filterResellerId || undefined,
      registrationFrom: filterRegistrationFrom || undefined,
      registrationTo: filterRegistrationTo || undefined,
      expiryFrom: filterExpiryFrom || undefined,
      expiryTo: filterExpiryTo || undefined,
      registrationMethod: filterRegistrationMethod !== 'all' ? filterRegistrationMethod : undefined,
      expiryPreset: filterExpiryPreset !== 'all' ? filterExpiryPreset : undefined,
      includeInactive: showInactive,
    }).then(setSystems);
    api.companies.list().then(setCompanies);
    api.branches.list().then(setAllBranches);
    api.resellers.list().then(setResellers);
    fetchLatestStatus();
    fetchAvailableSoftware();
    fetchUploadedXmlKeys();
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [filterResellerId, serverFiltersNonce, showInactive]);

  // Filter branches based on selected company filter
  const filteredBranchesForFilter = filterCompanyId 
    ? allBranches.filter(b => b.companyId === filterCompanyId)
    : allBranches;

  // Apply filters to systems
  const filteredSystems = systems.filter(s => {
    if (filterCompanyId && s.branch?.company?.id !== filterCompanyId) return false;
    if (filterBranchId && s.branchId !== filterBranchId) return false;
    if (filterResellerId && s.resellerId !== filterResellerId) return false;
    if (filterName && !s.systemName?.toLowerCase().includes(filterName.toLowerCase())) return false;
    if (filterMachineKey && !s.machineKey?.toLowerCase().includes(filterMachineKey.toLowerCase())) return false;
    if (filterVat && !s.vatNumber?.toLowerCase().includes(filterVat.toLowerCase())) return false;
    if (filterCrn && !s.crnNumber?.toLowerCase().includes(filterCrn.toLowerCase())) return false;
    if (filterZatcaStatus !== 'all') {
      if (filterZatcaStatus === 'active' && !s.isZatcaActivated) return false;
      if (filterZatcaStatus === 'inactive' && s.isZatcaActivated) return false;
    }
    return true;
  });

  const clearFilters = () => {
    setFilterCompanyId(null);
    setFilterBranchId(null);
    setFilterResellerId(null);
    setFilterName('');
    setFilterMachineKey('');
    setFilterVat('');
    setFilterCrn('');
    setFilterZatcaStatus('all');

    setFilterRegistrationFrom('');
    setFilterRegistrationTo('');
    setFilterExpiryFrom('');
    setFilterExpiryTo('');
    setFilterRegistrationMethod('all');
    setFilterExpiryPreset('all');

    setServerFiltersNonce(n => n + 1);
  };

  const applyServerFilters = () => {
    setServerFiltersNonce(n => n + 1);
  };

  useEffect(() => {
    const interval = setInterval(fetchLatestStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (didApplyQueryFilters.current) return;

    const companyIdParam = searchParams.get('companyId');
    const branchIdParam = searchParams.get('branchId');
    const machineKeyParam = searchParams.get('machineKey');

    if (companyIdParam) {
      const companyId = Number(companyIdParam);
      if (!Number.isNaN(companyId)) setFilterCompanyId(companyId);
    }

    if (branchIdParam) {
      const branchId = Number(branchIdParam);
      if (!Number.isNaN(branchId)) setFilterBranchId(branchId);
    }

    if (machineKeyParam) {
      setFilterMachineKey(machineKeyParam);
    }

    if (!companyIdParam && branchIdParam) {
      const branchId = Number(branchIdParam);
      const match = allBranches.find(b => b.id === branchId);
      if (match?.companyId) setFilterCompanyId(match.companyId);
    }

    if (companyIdParam || branchIdParam || machineKeyParam) {
      didApplyQueryFilters.current = true;
    }
  }, [searchParams, allBranches]);

  const handleCompanyChange = async (companyId: number) => {
    setSelectedCompanyId(companyId);
    const companyBranches = await api.branches.listByCompany(companyId);
    setBranches(companyBranches);
    const selectedCompany = companies.find(c => c.id === companyId);

    setForm(f => ({
      ...f,
      branchId: editId !== null ? companyBranches[0]?.id : undefined,
      companyNameSnapshot: (f.companyNameSnapshot ?? '').trim().length > 0
        ? f.companyNameSnapshot
        : (selectedCompany?.name ?? f.companyNameSnapshot),
      companyAddressSnapshot: (f.companyAddressSnapshot ?? '').trim().length > 0
        ? f.companyAddressSnapshot
        : (selectedCompany?.address ?? f.companyAddressSnapshot),
    }));
  };

  const openCreate = () => {
    setEditId(null);
    setShowCreateForm(true);
    setSelectedCompanyId(null);
    setBranches([]);

    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    const defaultExpiryDate = oneYearFromNow.toISOString().split('T')[0];

    setForm({
      hostname: '',
      description: '',
      licenseExpiryDate: defaultExpiryDate,
      vatNumber: '',
      crnNumber: '',
      companyNameSnapshot: '',
      companyAddressSnapshot: ''
    });
  };

  const openEdit = async (s: DeviceSystem) => {
    setEditId(s.id);
    const formattedDate = s.licenseExpiryDate ? new Date(s.licenseExpiryDate).toISOString().split('T')[0] : '';
    setForm({ ...s, licenseExpiryDate: formattedDate });
    setShowCreateForm(true);

    if (s.branch?.company) {
      setSelectedCompanyId(s.branch.company.id);
      const companyBranches = await api.branches.listByCompany(s.branch.company.id);
      setBranches(companyBranches);
    }
  };

  const validateForm = () => {
    return {
      company: !!selectedCompanyId,
      branch: !!form.branchId,
      licenseExpiryDate: !!form.licenseExpiryDate,
      systemName: !!form.systemName && form.systemName.trim().length > 0
    };
  };

  const handleSubmit = async () => {
    const v = validateForm();
    setValidation(v);
    if (!v.company || !v.branch || !v.licenseExpiryDate || !v.systemName) {
      notify({ type: 'error', message: 'Company, branch, system name, and license expiry are required.' });
      return;
    }

    try {
      if (editId == null) {
        await api.systems.create(form as DeviceSystem);
      } else {
        await api.systems.update(form as DeviceSystem);
      }

      load();
      setForm({});
      setEditId(null);
      setShowCreateForm(false);
      setSelectedCompanyId(null);
      setBranches([]);
      setValidation({
        company: true,
        branch: true,
        licenseExpiryDate: true,
        systemName: true
      });
    } catch (error: any) {
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert('An error occurred while saving the system.');
      }
    }
  };

  const handleDelete = async (id: number, systemName: string, machineKey: string, isActive?: boolean) => {
    const softDelete = isActive !== false;
    confirm({
      title: softDelete ? 'Deactivate System' : 'Permanently Delete System',
      message: softDelete
        ? `Deactivate "${systemName}" (${machineKey})? The system will be marked as deleted. Click delete again to permanently remove and delete logs.`
        : `Permanently delete "${systemName}" (${machineKey}) and remove all logs? This action cannot be undone.`,
      confirmText: softDelete ? 'Deactivate' : 'Delete Permanently',
      cancelText: 'Cancel',
      confirmButtonClass: softDelete ? 'btn btn-warning' : 'btn btn-danger',
      onConfirm: async () => {
        await api.systems.delete(id);
        load();
      }
    });
  };

  const handleGenerateEncryptionKey = async (systemId: number) => {
    if (!encryptionKeyExpiryDate) {
      notify({ type: 'error', message: 'Encryption key expiry date is required.' });
      return;
    }

    confirm({
      title: 'Generate Encryption Key',
      message: 'Are you sure you want to generate an encryption key for this system?\\n\\nThis action cannot be undone and only one encryption key per system is allowed.',
      confirmText: 'Generate Key',
      cancelText: 'Cancel',
      confirmButtonClass: 'btn btn-warning',
      onConfirm: async () => {
        try {
          const result = await api.systems.generateEncryptionKey(systemId, { expiryDate: encryptionKeyExpiryDate });
          setShowEncryptionKey({ systemId, keyData: result });
          setShowEncryptionKeyForm(false);
          setEncryptionKeyExpiryDate('');
          load();
        } catch (error: any) {
          if (error.response?.data?.error) {
            alert(error.response.data.error);
          } else {
            alert('An error occurred while generating the encryption key.');
          }
        }
      }
    });
  };

  const handleGenerateMachineKey = async (systemId: number) => {
    confirm({
      title: 'Generate Machine Key',
      message: 'Generate a backend-issued machine key for this system? This is only needed if the system does not already have one.',
      confirmText: 'Generate',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await api.systems.generateMachineKey(systemId);
          load();
        } catch (error: any) {
          if (error.response?.data?.error) {
            alert(error.response.data.error);
          } else {
            alert('An error occurred while generating the machine key.');
          }
        }
      }
    });
  };

  const handleUpdateToVersion = async (systemId: number, versionNumber: string) => {
    const latest = availableSoftware[0]?.versionNumber;
    const isLatest = latest ? latest.toLowerCase() === versionNumber.toLowerCase() : false;

    const latestLabel = latest ? ` (latest: ${latest})` : '';
    const targetLabel = `v${versionNumber}${isLatest ? ' (latest)' : ''}`;

    confirm({
      title: 'Update System Version',
      message: `Are you sure you want to configure this system to ${targetLabel}?${latestLabel}`,
      confirmText: 'Update',
      cancelText: 'Cancel',
      confirmButtonClass: 'btn btn-primary',
      onCancel: () => {
        setPendingUpdateSelection(prev => {
          const next = { ...prev };
          delete next[systemId];
          return next;
        });
      },
      onConfirm: async () => {
        setUpdating(prev => ({ ...prev, [systemId]: true }));

        try {
          const result: any = await api.systems.updateToVersion(systemId, { versionNumber });
          notify({
            type: 'success',
            message: `${result?.message || 'System configured version updated successfully.'} Previous: ${result?.previousVersion ?? 'N/A'} → New: ${result?.newConfiguredVersion ?? versionNumber}`
          });
          load();
        } catch (error: any) {
          notify({ type: 'error', message: error?.response?.data?.error || error?.message || 'Failed to update system version' });
          setPendingUpdateSelection(prev => {
            const next = { ...prev };
            delete next[systemId];
            return next;
          });
        } finally {
          setUpdating(prev => ({ ...prev, [systemId]: false }));
        }
      }
    });
  };

  const handleShowEncryptionKey = async (systemId: number) => {
    try {
      const keyData = await api.systems.getEncryptionKey(systemId);
      setShowEncryptionKey({ systemId, keyData });
    } catch (error: any) {
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert('An error occurred while retrieving the encryption key.');
      }
    }
  };

  const handleClearEncryptionKey = async (systemId: number) => {
    confirm({
      title: 'Clear Encryption Key',
      message: 'Are you sure you want to clear the encryption key for this system?\n\nThis will remove the encryption key from the system.',
      confirmText: 'Clear Key',
      cancelText: 'Cancel',
      confirmButtonClass: 'btn btn-danger',
      onConfirm: async () => {
        try {
          await api.systems.clearEncryptionKey(systemId);
          notify({ type: 'success', message: 'Encryption key cleared successfully.' });
          setShowEncryptionKey(null);
          load();
        } catch (error: any) {
          if (error.response?.data?.error) {
            alert(error.response.data.error);
          } else {
            alert('An error occurred while clearing the encryption key.');
          }
        }
      }
    });
  };

  const handleShowEncryptionKeyForm = async (systemId: number) => {
    setEditId(systemId);
    setShowEncryptionKeyForm(true);
    setEncryptionKeyExpiryDate('');
  };

  const cancelForm = () => {
    setForm({});
    setEditId(null);
    setShowCreateForm(false);
    setShowEncryptionKeyForm(false);
    setEncryptionKeyExpiryDate('');
    setSelectedCompanyId(null);
    setBranches([]);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Encryption key copied to clipboard!');
    });
  };

  return (
    <Stack gap={6}>
      <HStack justify="space-between" flexWrap="wrap" gap={3}>
        <Heading size="lg">Systems Management</Heading>
        <Button colorPalette="brand" onClick={openCreate} display="inline-flex" alignItems="center" gap={2}>
          <PlusIcon />
          Add New System
        </Button>
      </HStack>

      {/* Filters */}
      <Panel shadow="sm">
        <PanelHeader>
          <HStack justify="space-between" flexWrap="wrap" gap={2}>
            <Heading size="sm">Filters</Heading>
            <Button size="xs" variant="ghost" onClick={clearFilters}>Clear All</Button>
          </HStack>
        </PanelHeader>
        <PanelBody>
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 6, xl: 8 }} gap={3}>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>Company</Text>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  value={filterCompanyId || ''}
                  onChange={e => {
                    const val = e.target.value ? Number(e.target.value) : null;
                    setFilterCompanyId(val);
                    setFilterBranchId(null);
                  }}
                >
                  <option value="">All Companies</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>Branch</Text>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  value={filterBranchId || ''}
                  onChange={e => setFilterBranchId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">All Branches</option>
                  {filteredBranchesForFilter.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>Reseller</Text>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  value={filterResellerId || ''}
                  onChange={e => setFilterResellerId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">All Resellers</option>
                  {resellers.filter(r => r.isActive).map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>Name</Text>
              <Input
                size="sm"
                placeholder="Search..."
                value={filterName}
                onChange={e => setFilterName(e.target.value)}
              />
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>Machine Key</Text>
              <Input
                size="sm"
                placeholder="Search..."
                value={filterMachineKey}
                onChange={e => setFilterMachineKey(e.target.value)}
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>VAT Number</Text>
              <Input
                size="sm"
                placeholder="Search..."
                value={filterVat}
                onChange={e => setFilterVat(e.target.value)}
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>CRN Number</Text>
              <Input
                size="sm"
                placeholder="Search..."
                value={filterCrn}
                onChange={e => setFilterCrn(e.target.value)}
              />
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>Registration Method</Text>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  value={filterRegistrationMethod}
                  onChange={e => setFilterRegistrationMethod(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="manual">Manual</option>
                  <option value="Auto">Auto</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>Expiry Preset</Text>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  value={filterExpiryPreset}
                  onChange={e => setFilterExpiryPreset(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="thisWeek">This Week</option>
                  <option value="thisMonth">This Month</option>
                  <option value="nextMonth">Next Month</option>
                  <option value="twoMonthsToYear">Two Months To Year</option>
                  <option value="nextYear">Next Year</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>Registered From (Saudi)</Text>
              <Input
                size="sm"
                type="date"
                value={filterRegistrationFrom}
                onChange={e => setFilterRegistrationFrom(e.target.value)}
              />
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>Registered To (Saudi)</Text>
              <Input
                size="sm"
                type="date"
                value={filterRegistrationTo}
                onChange={e => setFilterRegistrationTo(e.target.value)}
              />
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>Expiry From (Saudi)</Text>
              <Input
                size="sm"
                type="date"
                value={filterExpiryFrom}
                onChange={e => setFilterExpiryFrom(e.target.value)}
              />
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>Expiry To (Saudi)</Text>
              <Input
                size="sm"
                type="date"
                value={filterExpiryTo}
                onChange={e => setFilterExpiryTo(e.target.value)}
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>ZATCA Status</Text>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  value={filterZatcaStatus}
                  onChange={e => setFilterZatcaStatus(e.target.value)}
                >
                  <option value="all">All Systems</option>
                  <option value="active">ZATCA Active</option>
                  <option value="inactive">ZATCA Inactive</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>Deactivated</Text>
              <Checkbox.Root
                size="sm"
                checked={showInactive}
                onCheckedChange={(details: { checked: boolean | "indeterminate" }) => setShowInactive(details.checked === true)}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
                <Checkbox.Label>Show deactivated systems</Checkbox.Label>
              </Checkbox.Root>
            </Box>
          </SimpleGrid>

          <HStack justify="flex-end" mt={3} gap={2}>
            <Button size="sm" variant="outline" onClick={clearFilters}>
              Clear
            </Button>
            <Button size="sm" colorPalette="blue" onClick={applyServerFilters}>
              Apply
            </Button>
          </HStack>
        </PanelBody>
      </Panel>

      <Panel shadow="md">
        <PanelHeader>
          <HStack justify="space-between">
            <Heading size="md">Systems Directory</Heading>
            <Text fontSize="sm" color="gray.500">{filteredSystems.length} of {systems.length} systems</Text>
          </HStack>
        </PanelHeader>
        <PanelBody>
          <Box overflow="auto" maxH="60vh">
          <Table.Root variant="line" size="sm">
          <Table.Header position="sticky" top="0" bg="white" zIndex={1}>
            <Table.Row>
              <Table.ColumnHeader whiteSpace="nowrap">Name</Table.ColumnHeader>
              <Table.ColumnHeader whiteSpace="nowrap">Branch</Table.ColumnHeader>
              <Table.ColumnHeader whiteSpace="nowrap">Reseller</Table.ColumnHeader>
              <Table.ColumnHeader whiteSpace="nowrap">Machine Key</Table.ColumnHeader>
              <Table.ColumnHeader whiteSpace="nowrap">Host</Table.ColumnHeader>
              <Table.ColumnHeader whiteSpace="nowrap">Description</Table.ColumnHeader>
              <Table.ColumnHeader whiteSpace="nowrap">License Expiry</Table.ColumnHeader>
              <Table.ColumnHeader whiteSpace="nowrap">Last Updated</Table.ColumnHeader>
              <Table.ColumnHeader whiteSpace="nowrap">Update Version</Table.ColumnHeader>
              <Table.ColumnHeader whiteSpace="nowrap">VAT Number</Table.ColumnHeader>
              <Table.ColumnHeader whiteSpace="nowrap">CRN Number</Table.ColumnHeader>
              <Table.ColumnHeader whiteSpace="nowrap">ZATCA</Table.ColumnHeader>
              <Table.ColumnHeader whiteSpace="nowrap">Registered</Table.ColumnHeader>
              <Table.ColumnHeader whiteSpace="nowrap">Encryption Key</Table.ColumnHeader>
              <Table.ColumnHeader whiteSpace="nowrap">Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filteredSystems.map(s => (
              <Table.Row key={s.id} opacity={s.isActive === false ? 0.6 : 1}>
                <Table.Cell>{s.systemName}</Table.Cell>
                <Table.Cell>{s.branch ? `[${s.branch.company?.name ?? 'N/A'}] ${s.branch.name}` : 'Loading...'}</Table.Cell>
                <Table.Cell>{s.reseller?.name || <Text color="gray.500">-</Text>}</Table.Cell>
                <Table.Cell>
                  {s.isActive === false && (
                    <Badge colorPalette="red" mb={1}>Deleted</Badge>
                  )}
                  {s.machineKey ? (
                    <Box>
                      <HStack gap={1} align="center">
                        <Code fontSize="xs" maxW="150px" display="block" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={s.machineKey}>{s.machineKey}</Code>
                        <Button size="xs" variant="outline" onClick={() => copyToClipboard(s.machineKey!)} title="Copy machine key">
                          Copy
                        </Button>
                      </HStack>
                      {s.registrationMethod && (
                        <Badge
                          mt={1}
                          size="sm"
                          colorPalette={
                            s.registrationMethod === 'Auto'
                              ? 'purple'
                              : s.registrationMethod === 'manual'
                                ? 'gray'
                                : 'gray'
                          }
                        >
                          {s.registrationMethod}
                        </Badge>
                      )}
                    </Box>
                  ) : (
                    <Button size="xs" variant="outline" colorPalette="blue" onClick={() => handleGenerateMachineKey(s.id)}>
                      Generate Machine Key
                    </Button>
                  )}
                </Table.Cell>
                <Table.Cell>{s.hostname}</Table.Cell>
                <Table.Cell>{s.description}</Table.Cell>
                <Table.Cell>
                  <Box>
                    <Text fontWeight="medium">{formatSaudiDate(s.licenseExpiryDate)}</Text>
                    {(() => {
                      const daysLeft = getDaysUntilExpiry(s.licenseExpiryDate);
                      if (daysLeft < 0) {
                        return <Text fontSize="xs" color="red.500">Expired {Math.abs(daysLeft)} days ago</Text>;
                      } else if (daysLeft === 0) {
                        return <Text fontSize="xs" color="orange.500">Expires today</Text>;
                      } else if (daysLeft <= 30) {
                        return <Text fontSize="xs" color="orange.500">{daysLeft} days left</Text>;
                      } else {
                        return <Text fontSize="xs" color="green.500">{daysLeft} days left</Text>;
                      }
                    })()}
                  </Box>
                </Table.Cell>
                <Table.Cell>
                  {s.machineKey && latestStatus[s.machineKey] ? (
                    <Box>
                      <Text>{timeAgoSaudi(latestStatus[s.machineKey].timeStamp)}</Text>
                      {latestStatus[s.machineKey].currentVersion && (
                        <Text fontSize="sm" fontWeight="bold" color={(() => {
                          const currentVersion = latestStatus[s.machineKey].currentVersion;
                          const latestVersion = availableSoftware[0]?.versionNumber;
                          
                          if (!latestVersion || !currentVersion) return "gray.500";
                          
                          // Parse version numbers (e.g., "1.2.3" -> [1, 2, 3])
                          const parseCurrent = currentVersion.split('.').map(Number);
                          const parseLatest = latestVersion.split('.').map(Number);
                          
                          // Calculate version difference
                          let versionDiff = 0;
                          for (let i = 0; i < Math.max(parseCurrent.length, parseLatest.length); i++) {
                            const current = parseCurrent[i] || 0;
                            const latest = parseLatest[i] || 0;
                            if (latest > current) {
                              versionDiff += (latest - current) * Math.pow(100, Math.max(parseCurrent.length, parseLatest.length) - i - 1);
                            } else if (current > latest) {
                              break; // Current is ahead (shouldn't happen normally)
                            }
                          }
                          
                          if (currentVersion === latestVersion) return "green.500";
                          if (versionDiff >= 10) return "red.500";
                          return "orange.500";
                        })()}>
                          (v{latestStatus[s.machineKey].currentVersion})
                        </Text>
                      )}
                      {(() => {
                        try {
                          const parsed = JSON.parse(latestStatus[s.machineKey].statusJson);
                          if (parsed.domain) {
                            return (
                              <Text fontSize="xs" color="blue.500" wordBreak="break-all">
                                {parsed.domain}
                              </Text>
                            );
                          }
                        } catch {}
                        return null;
                      })()}
                    </Box>
                  ) : (
                    <Text color="gray.500">Never</Text>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Box>
                    {(() => {
                      const currentVersion = s.configuredVersion;

                      const findOptionValueForVersion = (version?: string) => {
                        if (!version) return '';
                        const normalized = version.trim().toLowerCase();
                        const match = availableSoftware.find(v => v.isActive && (v.versionNumber || '').trim().toLowerCase() === normalized);
                        return match ? match.versionNumber : '';
                      };

                      const selectedValue = pendingUpdateSelection[s.id] ?? findOptionValueForVersion(currentVersion);

                      return (
                        <Menu.Root>
                          <Menu.Trigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              w="160px"
                              justifyContent="space-between"
                              fontSize="sm"
                              fontWeight="normal"
                              bg={(() => {
                                const match = availableSoftware.find(v => v.versionNumber === selectedValue);
                                if (!match) return undefined;
                                return match.releaseType === 'Test' ? '#fef3c7' : '#dcfce7';
                              })()}
                              _hover={{
                                bg: (() => {
                                  const match = availableSoftware.find(v => v.versionNumber === selectedValue);
                                  if (!match) return undefined;
                                  return match.releaseType === 'Test' ? '#fde68a' : '#bbf7d0';
                                })()
                              }}
                              _active={{
                                bg: (() => {
                                  const match = availableSoftware.find(v => v.versionNumber === selectedValue);
                                  if (!match) return undefined;
                                  return match.releaseType === 'Test' ? '#fde68a' : '#bbf7d0';
                                })()
                              }}
                            >
                              <Text truncate>
                                {selectedValue
                                  ? `v${selectedValue}`
                                  : 'Select version'}
                              </Text>
                              ▾
                            </Button>
                          </Menu.Trigger>
                          <Portal>
                            <Menu.Positioner>
                              <Menu.Content minW="260px" maxH="320px" overflowY="auto">
                                {availableSoftware.filter(v => v.isActive).map((version, idx) => (
                                  <Menu.Item
                                    key={version.id}
                                    value={version.versionNumber}
                                    onClick={() => {
                                      setPendingUpdateSelection(prev => ({ ...prev, [s.id]: version.versionNumber }));
                                      handleUpdateToVersion(s.id, version.versionNumber);
                                    }}
                                    bg={version.releaseType === 'Test' ? '#fef3c7' : '#dcfce7'}
                                    _hover={{ filter: 'brightness(0.95)' }}
                                    px={3}
                                    py={2}
                                  >
                                    <Stack gap={0}>
                                      <Text fontSize="sm" fontWeight="medium" color={version.releaseType === 'Test' ? '#92400e' : '#166534'}>
                                        v{version.versionNumber}{idx === 0 ? ' (latest)' : ''}
                                      </Text>
                                      {version.description && (
                                        <Text fontSize="xs" color="gray.600" lineClamp={2}>
                                          {version.description}
                                        </Text>
                                      )}
                                    </Stack>
                                  </Menu.Item>
                                ))}
                              </Menu.Content>
                            </Menu.Positioner>
                          </Portal>
                        </Menu.Root>
                      );
                    })()}
                    {updating[s.id] && (
                        <Box mt={1}>
                          <Text fontSize="sm" color="blue.500">
                            <Spinner size="sm" mr={1} /> Updating...
                          </Text>
                        </Box>
                      )}
                  </Box>
                </Table.Cell>
                <Table.Cell>{s.vatNumber || <Text color="gray.500">N/A</Text>}</Table.Cell>
                <Table.Cell>{s.crnNumber || <Text color="gray.500">N/A</Text>}</Table.Cell>
                <Table.Cell>
                  {s.isZatcaActivated === null || s.isZatcaActivated === undefined ? (
                    <Badge colorPalette="gray">Not Available</Badge>
                  ) : s.isZatcaActivated ? (
                    <Badge colorPalette="green">Active</Badge>
                  ) : (
                    <Badge colorPalette="gray">Inactive</Badge>
                  )}
                </Table.Cell>
                <Table.Cell>{s.registeredAtSaudi ? formatSaudiDate(s.registeredAtSaudi) : formatSaudiDate(s.registeredAtUtc)}</Table.Cell>
                <Table.Cell>
                  {s.encryptionKey ? (
                    <Stack gap={1}>
                      <Badge colorPalette="green">Generated</Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        colorPalette="blue"
                        onClick={() => handleShowEncryptionKey(s.id)}
                      >
                        View Key
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        colorPalette="red"
                        onClick={() => handleClearEncryptionKey(s.id)}
                      >
                        Clear Key
                      </Button>
                    </Stack>
                  ) : (
                    <Button
                      size="sm"
                      colorPalette="yellow"
                      onClick={() => handleShowEncryptionKeyForm(s.id)}
                    >
                      Create Key
                    </Button>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <HStack gap={1}>
                    {s.machineKey && (
                      <Box position="relative" display="inline-block">
                        <Button size="sm" variant="outline" colorPalette="teal" onClick={() => setXmlArchiveModal({ machineKey: s.machineKey!, systemName: s.systemName || 'Unnamed' })} title="ZATCA XML Records">
                          XML
                        </Button>
                        {uploadedXmlKeys.has(s.machineKey) && (
                          <Box
                            position="absolute"
                            top="-2px"
                            right="-2px"
                            w="8px"
                            h="8px"
                            borderRadius="full"
                            bg="green.400"
                            border="1px solid white"
                            title="At least one XML uploaded"
                          />
                        )}
                      </Box>
                    )}
                    {s.machineKey && (
                      <Button size="sm" variant="outline" colorPalette="purple" onClick={() => setTaxSummaryModal({ machineKey: s.machineKey!, systemName: s.systemName || 'Unnamed' })} title="Tax Summary">
                        Tax
                      </Button>
                    )}
                    <Button size="sm" variant="outline" colorPalette="blue" onClick={() => openEdit(s)} title="Edit">
                      <EditIcon />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      colorPalette={s.isActive === false ? 'red' : 'yellow'}
                      onClick={() => handleDelete(s.id, s.systemName || 'Unnamed System', s.machineKey || 'N/A', s.isActive)}
                      title={s.isActive === false ? 'Delete Permanently' : 'Delete'}
                    >
                      <TrashIcon />
                    </Button>
                  </HStack>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
        </Box>
        </PanelBody>
      </Panel>

      {/* Add/Edit System Modal */}
      <SimpleModal
        isOpen={showCreateForm}
        onClose={cancelForm}
        title={editId == null ? 'Add New System' : 'Edit System'}
        size="lg"
        footer={
          <HStack gap={2} justify="flex-end">
            <Button variant="outline" onClick={cancelForm}>
              Cancel
            </Button>
            <Button colorPalette="blue" onClick={handleSubmit}>
              {editId == null ? 'Create System' : 'Update System'}
            </Button>
          </HStack>
        }
      >
        <Stack gap={3}>
          <FormControl isRequired isInvalid={!validation.company}>
            <FormLabel>Company</FormLabel>
            <NativeSelect.Root disabled={editId !== null}>
              <NativeSelect.Field
                value={selectedCompanyId || ''}
                onChange={e => handleCompanyChange(Number(e.target.value))}
              >
                <option value="">Choose a company...</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            <FormErrorMessage>Company is required.</FormErrorMessage>
          </FormControl>

          {branches.length > 0 && (
            <FormControl isRequired isInvalid={!validation.branch}>
              <FormLabel>Branch</FormLabel>
              <NativeSelect.Root disabled={editId !== null}>
                <NativeSelect.Field
                  value={form.branchId || ''}
                  onChange={e => setForm(f => ({ ...f, branchId: Number(e.target.value) }))}
                >
                  <option value="">Choose a branch...</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name} - {b.location}</option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <FormErrorMessage>Branch is required.</FormErrorMessage>
            </FormControl>
          )}

          {editId !== null && (
            <FormControl>
              <FormLabel>Machine Key</FormLabel>
              <Input value={form.machineKey || 'Not yet generated'} readOnly />
              <FormHelperText>Machine key is generated by the server.</FormHelperText>
            </FormControl>
          )}

          <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
            <FormControl isRequired isInvalid={!validation.systemName}>
              <FormLabel>Name</FormLabel>
              <Input
                value={form.systemName || ''}
                onChange={e => setForm(f => ({ ...f, systemName: e.target.value }))}
                placeholder="Enter name"
              />
              <FormErrorMessage>Name is required.</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel>Company Name (Snapshot)</FormLabel>
              <Input
                value={form.companyNameSnapshot || ''}
                onChange={e => setForm(f => ({ ...f, companyNameSnapshot: e.target.value }))}
                placeholder="Company name used for encryption"
              />
              <FormHelperText>Used for encryption/decryption; independent of Companies dropdown.</FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel>Host (Localhost or IP)</FormLabel>
              <Input
                value={form.hostname || ''}
                onChange={e => setForm(f => ({ ...f, hostname: e.target.value }))}
                placeholder="e.g., localhost, 192.168.1.100"
              />
            </FormControl>

            <FormControl>
              <FormLabel>VAT Number</FormLabel>
              <Input
                value={form.vatNumber || ''}
                onChange={e => setForm(f => ({ ...f, vatNumber: e.target.value }))}
                placeholder="e.g., VAT123456789"
              />
            </FormControl>

            <FormControl>
              <FormLabel>CRN Number</FormLabel>
              <Input
                value={form.crnNumber || ''}
                onChange={e => setForm(f => ({ ...f, crnNumber: e.target.value }))}
                placeholder="e.g., CRN987654321"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Company Address (Snapshot)</FormLabel>
              <Input
                value={form.companyAddressSnapshot || ''}
                onChange={e => setForm(f => ({ ...f, companyAddressSnapshot: e.target.value }))}
                placeholder="Company address used for encryption"
              />
              <FormHelperText>Editable; does not affect filtering.</FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel>Reseller</FormLabel>
              <NativeSelect.Root size="md">
                <NativeSelect.Field
                  value={form.resellerId || ''}
                  onChange={e => setForm(f => ({ ...f, resellerId: e.target.value ? Number(e.target.value) : undefined }))}
                >
                  <option value="">No Reseller</option>
                  {resellers.filter(r => r.isActive).map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <FormHelperText>Optional - Assign this system to a reseller</FormHelperText>
            </FormControl>
          </SimpleGrid>

          <FormControl isRequired isInvalid={!validation.licenseExpiryDate}>
            <FormLabel>License Expiry Date</FormLabel>
            <Input
              type="date"
              value={form.licenseExpiryDate || ''}
              onChange={e => setForm(f => ({ ...f, licenseExpiryDate: e.target.value }))}
            />
            {form.licenseExpiryDate && (
              <FormHelperText>Duration: {calculateLicenseDuration(form.licenseExpiryDate)}</FormHelperText>
            )}
            <FormErrorMessage>License Expiry Date is required.</FormErrorMessage>
          </FormControl>

          <FormControl>
            <FormLabel>Description</FormLabel>
            <Textarea
              value={form.description || ''}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Enter system description"
              rows={2}
            />
          </FormControl>

          {editId !== null && (
            <FormControl>
              <Checkbox.Root
                checked={!!form.isUploadEnabled}
                onCheckedChange={(details: { checked: boolean | "indeterminate" }) => setForm(f => ({ ...f, isUploadEnabled: details.checked === true }))}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
                <Checkbox.Label>Enable XML upload</Checkbox.Label>
              </Checkbox.Root>
              <FormHelperText>
                Controls whether this system may upload ZATCA XML backups. Off by default for every
                registered system — the device itself can never turn this on; it can only be enabled here.
              </FormHelperText>
            </FormControl>
          )}

          {editId === null && (
            <Alert.Root status="info">
              <Alert.Content>
                <Alert.Description fontSize="sm">
                  Machine key will be generated automatically by the backend after save.
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          )}
        </Stack>
      </SimpleModal>

      {/* Encryption Key Generation Form Modal */}
      <SimpleModal
        isOpen={showEncryptionKeyForm}
        onClose={cancelForm}
        title="Generate Encryption Key"
        size="md"
        footer={
          <HStack gap={2} justify="flex-end">
            <Button variant="outline" onClick={cancelForm}>
              Cancel
            </Button>
            <Button colorPalette="yellow" onClick={() => editId && handleGenerateEncryptionKey(editId)}>
              Generate Key
            </Button>
          </HStack>
        }
      >
        <Stack gap={4}>
          <FormControl isRequired>
            <FormLabel>Encryption Key Expiry Date</FormLabel>
            <Input
              type="date"
              value={encryptionKeyExpiryDate}
              onChange={e => setEncryptionKeyExpiryDate(e.target.value)}
            />
            <FormHelperText>
              Set the expiry date for this encryption key. After this date, the inventory software will need a new key.
            </FormHelperText>
          </FormControl>
          
          <Alert.Root status="info">
            <Alert.Content>
              <Alert.Description fontSize="sm">
                The encryption key will be generated using the system's machine key. Make sure to save this key securely after generation.
              </Alert.Description>
            </Alert.Content>
          </Alert.Root>
        </Stack>
      </SimpleModal>

      {/* Encryption Key Display Modal */}
      <SimpleModal
        isOpen={!!showEncryptionKey}
        onClose={() => setShowEncryptionKey(null)}
        title="Encryption Key"
        size="lg"
        footer={
          <Button variant="outline" onClick={() => setShowEncryptionKey(null)}>Close</Button>
        }
      >
        {showEncryptionKey && (
          <Stack gap={4}>
            <FormControl>
              <FormLabel>Generated At:</FormLabel>
              <Text>{showEncryptionKey.keyData.generatedAt ? new Date(showEncryptionKey.keyData.generatedAt).toLocaleString('en-GB', { timeZone: 'Asia/Riyadh' }) : 'N/A'}</Text>
            </FormControl>

            <FormControl>
              <FormLabel>Expiry Date:</FormLabel>
              <Text>{showEncryptionKey.keyData.expiryDate ? formatSaudiDate(showEncryptionKey.keyData.expiryDate) : 'N/A'}</Text>
            </FormControl>

            <FormControl>
              <FormLabel>Display Key:</FormLabel>
              <Code>{showEncryptionKey.keyData.displayKey}</Code>
            </FormControl>

            <FormControl>
              <FormLabel>Full Encryption Key:</FormLabel>
              <HStack>
                <Input value={showEncryptionKey.keyData.encryptionKey} readOnly flex={1} />
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(showEncryptionKey.keyData.encryptionKey)}
                >
                  Copy
                </Button>
              </HStack>
            </FormControl>

            <Alert.Root status="info">
              <Alert.Content>
                <Alert.Description>
                  <strong>Note:</strong> This encryption key is generated from the machine key + company name + VAT (optional) + CRN (Optional) only. Use this key instead of the machine key when uploading status data.
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          </Stack>
        )}
      </SimpleModal>

      {/* ZATCA XML Records Modal */}
      <ZatcaXmlArchiveModal
        isOpen={xmlArchiveModal !== null}
        onClose={() => setXmlArchiveModal(null)}
        machineKey={xmlArchiveModal?.machineKey ?? ''}
        systemName={xmlArchiveModal?.systemName ?? ''}
      />

      {/* ZATCA Tax Summary Modal */}
      <ZatcaTaxSummaryModal
        isOpen={taxSummaryModal !== null}
        onClose={() => setTaxSummaryModal(null)}
        machineKey={taxSummaryModal?.machineKey ?? ''}
        systemName={taxSummaryModal?.systemName ?? ''}
      />
    </Stack>
  );
};

export default SystemsPage;
